import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, SearchIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface CompleteUseCaseDiscoveryToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

const CompleteUseCaseDiscoveryToolCallComponent: React.FC<CompleteUseCaseDiscoveryToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  let summary = '';
  try {
    const parsed = typeof args === 'object' && args ? args : JSON.parse(argsText);
    summary = parsed?.summary || '';
  } catch {
    summary = '';
  }

  let isSuccess = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Failed to save discovery notes';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Saving use case discovery notes...
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Use Case Discovery
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    {errorMessage || 'Failed to save discovery notes'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Use Case Discovery
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Discovery notes saved — use case approved
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {summary && !isRunning && !hasError && (
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {summary}
          </p>
        )}
      </div>
    </Card>
  );
};

export const CompleteUseCaseDiscoveryToolCall = CompleteUseCaseDiscoveryToolCallComponent as React.ComponentType<any>;
